import API from "./api";
import { onMessage, sendMessage, isWSConnected } from "./wsService";

export const getFreshPremiumData = async (accountSetId) => {
  try {
    const response = await API.get(`/trading/fresh-premium/${accountSetId}`);
    return response.data;
  } catch (error) {
    console.error("Failed to get fresh premium data:", error);
    throw error;
  }
};

class MT4MT5Service {
  constructor() {
    this.dataCache = new Map();
    this.listeners = new Set();
    this.accountSetConfigs = new Map();
    this.premiumData = new Map();
    this.wsSubscriptions = new Map();
    this.initialized = false;
  }

  initializeWebSocketSubscriptions() {
    if (this.initialized) return;

    const unsubscribePositions = onMessage("positions_update", (data) => {
      if (data.accountSetId) {
        this.dataCache.set(data.accountSetId, {
          mt5Data: data.mt5Data || [],
          mt4Data: data.mt4Data || [],
        });
        this.notifyListeners();
      }
    });

    const unsubscribePremium = onMessage("premium_update", (data) => {
      if (data.accountSetId) {
        this.premiumData.set(data.accountSetId, {
          sellPremium:
            typeof data.sellPremium === "number" ? data.sellPremium : Number(data.sellpremium || 0),
          buyPremium:
            typeof data.buyPremium === "number" ? data.buyPremium : Number(data.buypremium || 0),
          futureQuote: data.futureQuote,
          spotQuote: data.spotQuote,
          timestamp: data.timestamp,
          premiumLive: Boolean(data.premiumLive),
          collectorStale: Boolean(data.collectorStale),
        });
        this.notifyListeners();
      }
    });

    const unsubscribeTradeStatus = onMessage("trade_status_update", (data) => {
      this.notifyListeners({ tradeStatusUpdate: data });
    });

    this.globalUnsubscribes = [unsubscribePositions, unsubscribePremium, unsubscribeTradeStatus];
    this.initialized = true;
  }

  async initializeAccountSet(accountSetId) {
    try {
      this.initializeWebSocketSubscriptions();
      const response = await API.get(`/account-sets/${accountSetId}`);
      const accountSet = response.data.data || response.data;

      if (!accountSet || !accountSet.brokers || accountSet.brokers.length < 2) {
        console.error("❌ Account set validation failed:", {
          accountSetExists: !!accountSet,
          brokersExists: !!accountSet?.brokers,
          brokersCount: accountSet?.brokers?.length || 0,
          accountSetId,
        });
        return;
      }

      const mt5Broker = accountSet.brokers.find((b) => b.terminal === "MT5");
      const mt4Broker = accountSet.brokers.find((b) => b.terminal === "MT4");

      if (!mt5Broker || !mt4Broker) {
        console.warn("Missing MT4 or MT5 broker in account set:", accountSetId);
        return;
      }

      this.accountSetConfigs.set(accountSetId, {
        mt5Id: mt5Broker.token,
        mt4Id: mt4Broker.token,
        futureSymbol: accountSet.futureSymbol,
        spotSymbol: accountSet.spotSymbol,
        mt5Broker,
        mt4Broker,
      });
      await this.subscribeToWebSocketUpdates(accountSetId);
    } catch (error) {
      console.error("Error initializing account set:", error);
    }
  }

  async subscribeToWebSocketUpdates(accountSetId) {
    if (!isWSConnected()) {
      console.warn("WebSocket not connected, cannot subscribe to positions:", accountSetId);
      return;
    }

    const success = sendMessage({
      action: "subscribe_positions",
      accountSetId: accountSetId,
    });

    if (!success) {
      console.error("❌ Failed to send subscribe_positions message");
    }
  }

  isTradeStillOpen(accountSetId, mt5Ticket, mt4Ticket) {
    const mt5Trade = this.getMT5TradeData(accountSetId, mt5Ticket);
    const mt4Trade = this.getMT4TradeData(accountSetId, mt4Ticket);

    const mt5Open = mt5Trade !== null;
    const mt4Open = mt4Trade !== null;

    return mt5Open || mt4Open;
  }

  calculateTradeProfit(accountSetId, mt5Ticket, mt4Ticket) {
    const mt5Trade = this.getMT5TradeData(accountSetId, mt5Ticket);
    const mt4Trade = this.getMT4TradeData(accountSetId, mt4Ticket);

    const mt5Profit = mt5Trade ? mt5Trade.profit || 0 : 0;
    const mt4Profit = mt4Trade ? mt4Trade.profit || 0 : 0;
    const mt5Swap = mt5Trade ? mt5Trade.swap || 0 : 0;
    const mt4Swap = mt4Trade ? mt4Trade.swap || 0 : 0;

    const result = {
      mt5Profit,
      mt4Profit,
      mt5Swap,
      mt4Swap,
      totalProfit: mt5Profit + mt4Profit + mt5Swap + mt4Swap,
      totalSwap: mt5Swap + mt4Swap,
    };

    return result;
  }

  setPremiumData(accountSetId, premiumData) {
    this.premiumData.set(accountSetId, premiumData);
    this.notifyListeners();
  }

  getPremiumData(accountSetId) {
    return this.premiumData.get(accountSetId) || null;
  }

  calculatePremiumData(accountSetId, direction, openingPremium) {
    const premiumData = this.getPremiumData(accountSetId);
    if (!premiumData) {
      return {
        currentPremium: 0,
        deficitPremium: 0,
      };
    }

    const currentPremium =
      direction === "Buy" ? premiumData.sellPremium || 0 : premiumData.buyPremium || 0;

    const deficitPremium = currentPremium - parseFloat(openingPremium || 0);

    return {
      currentPremium,
      deficitPremium,
    };
  }

  subscribe(callback) {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  notifyListeners(additionalData = {}) {
    this.listeners.forEach((callback) => {
      try {
        callback({
          dataCache: this.dataCache,
          premiumData: this.premiumData,
          ...additionalData,
        });
      } catch (error) {
        console.error("Error in listener callback:", error);
      }
    });
  }

  disconnect() {
    if (this.globalUnsubscribes) {
      this.globalUnsubscribes.forEach((unsubscribe) => unsubscribe());
      this.globalUnsubscribes = [];
    }
    this.dataCache.clear();
    this.accountSetConfigs.clear();
    this.premiumData.clear();
    this.wsSubscriptions.clear();
    this.initialized = false;
  }

  async getFreshPremiumData(accountSetId) {
    try {
      const response = await getFreshPremiumData(accountSetId);
      return response;
    } catch (error) {
      console.error("MT4MT5Service: Failed to get fresh premium data:", error);
      throw error;
    }
  }

  connect() {
    return undefined;
  }

  getMT5TradeData(...args) {
    if (args.length === 1) {
      return null;
    }
    const [accountSetId, ticket] = args;
    if (!ticket) return null;

    const cache = this.dataCache.get(accountSetId);
    if (!cache || !cache.mt5Data) return null;
    return cache.mt5Data.find((trade) => trade.ticket === parseInt(ticket));
  }

  getMT4TradeData(...args) {
    if (args.length === 1) {
      return null;
    }
    const [accountSetId, ticket] = args;
    if (!ticket) return null;

    const cache = this.dataCache.get(accountSetId);
    if (!cache || !cache.mt4Data) return null;
    return cache.mt4Data.find((trade) => parseInt(trade.ticket) === parseInt(ticket));
  }
}

const mt4mt5Service = new MT4MT5Service();

export default mt4mt5Service;
